import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { getActiveEvent, uploadLocation } from "../../api/event.api";
import LanIcon from "@mui/icons-material/Lan";
import "./Grid.css";
import { getUsersLight } from "../../api/user.api";
import UserCard from "../../components/cards/user.card";
import { useSnackbar } from "../../context/snackbar.context";
import { useAuth } from "../../context/auth.context";
import { useTheme } from "@mui/material/styles";

const LocationPage = () => {
  const [eventId, setEventId] = useState(null);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(false);
  const [config, setConfig] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [open, setOpen] = useState(false);
  const showSnackbar = useSnackbar();
  const auth = useAuth();
  const squares = Array.from({ length: 20 * 10 });
  const theme = useTheme();
  const borderColor = theme.palette.background.default;

  useEffect(() => {
    getActiveEvent()
      .then((response) => {
        const event = response.data;
        setEventId(event.id);

        if (event.location && typeof event.location === "string") {
          try {
            const parsedConfig = JSON.parse(event.location);
            setConfig(parsedConfig);
          } catch (e) {
            console.error("Error parsing location config:", e);
          }
        }

        getUsersLight(event.participants)
          .then((response) => setUsers(response.data))
          .catch((e) => console.log(e));
      })
      .catch((err) => console.error("Error loading event:", err));
  }, []);

  const onClick = (index) => {
    if (!edit) {
      return;
    }
    if (
      selectedType === "set" &&
      !(config[index] === "" || config[index] === undefined)
    ) {
      setSelectedIndex(index);
      setOpen(true);
    } else if (selectedType !== "set") {
      const updatedConfig = [...config];
      updatedConfig[index] = {
        type: selectedType,
      };
      setConfig(updatedConfig);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedIndex(null);
  };

  const toggleEdit = async () => {
    if (edit) {
      if (!eventId) {
        showSnackbar("No active Event!", "error");
        return;
      }

      try {
        setIsSaving(true);

        await uploadLocation(eventId, JSON.stringify(config));

        showSnackbar("Location configuration saved successfully!", "success");
      } catch (error) {
        showSnackbar(
          "Failed to save configuration. Please try again.",
          "error"
        );
      } finally {
        setIsSaving(false);
      }
    }

    setEdit((prev) => !prev);
  };

  const handleClear = () => {
    setConfig([]);
    setSelectedIndex(null);
  };

  const handleNameChange = (e) => {
    const { value } = e.target;
    setConfig((prev) =>
      prev.map((item, i) =>
        i === selectedIndex ? { ...item, name: value } : item
      )
    );
  };

  const handleParticipantChange = (e) => {
    const { value } = e.target;
    if (!value) {
      setConfig((prev) =>
        prev.map((item, i) =>
          i === selectedIndex ? { ...item, participantId: null } : item
        )
      );
      return;
    }

    const selectedUser = users.find((u) => u.id === value);
    setConfig((prev) =>
      prev.map((item, i) =>
        i === selectedIndex
          ? {
            ...item,
            participantId: value,
            name: selectedUser?.nickname || item.name,
          }
          : item
      )
    );
  };

  const handleTableNumberChange = (e) => {
    const { value } = e.target;
    setConfig((prev) =>
      prev.map((item, i) =>
        i === selectedIndex ? { ...item, number: value } : item
      )
    );
  };

  const handleTableBorderChange = (side) => (e) => {
    const checked = e.target.checked;
    setConfig((prev) =>
      prev.map((item, i) =>
        i === selectedIndex
          ? {
            ...item,
            borders: { ...item.borders, [side]: checked },
          }
          : item
      )
    );
  };

  const getSquareClass = () => {
    const classes = ["square"];
    if (edit) classes.push("square-edit");
    return classes.join(" ");
  };

  return (
    <div className="dashboard-container">
      <Typography variant="h3" gutterBottom>
        Location
      </Typography>

      {auth && auth.user && auth.user.role == "Admin" &&
        <Box display="flex" gap={1} alignItems="center">
          <Button
            onClick={toggleEdit}
            variant="contained"
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : edit ? "Save" : "Edit"}
          </Button>

          {edit && (
            <Button
              onClick={handleClear}
              variant="outlined"
              color="error"
              disabled={isSaving}
            >
              Clear
            </Button>
          )}
        </Box>}

      <Divider sx={{ mt: 1, mb: 1 }} />

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent={edit ? "flex-start" : "flex-start"}
        gap={edit ? 3 : 0}
      >
        {edit && (
          <Card sx={{ padding: 2, width: 180, flexShrink: 0 }}>
            <Typography gutterBottom>Edit Mode</Typography>
            <Divider sx={{ mb: 1 }} />

            <div>
              <Radio
                checked={selectedType === "set"}
                onClick={() => setSelectedType("set")}
              />{" "}
              Set Value
            </div>
            <div>
              <Radio
                checked={selectedType === "table"}
                onClick={() => setSelectedType("table")}
              />{" "}
              Table
            </div>
            <div>
              <Radio
                checked={selectedType === "seat"}
                onClick={() => setSelectedType("seat")}
              />{" "}
              Seat
            </div>
            <div>
              <Radio
                checked={selectedType === "switch"}
                onClick={() => setSelectedType("switch")}
              />{" "}
              Switch
            </div>
            <div>
              <Radio
                checked={selectedType === ""}
                onClick={() => setSelectedType("")}
              />{" "}
              Remove
            </div>
          </Card>
        )}

        {/* Sitzplan */}
        <Box
          className="grid"
          sx={{
            flexGrow: 1,
            justifyContent: edit ? "flex-start" : "flex-start",
          }}
        >
          {squares.map((_, i) => (
            <div
              key={i}
              className={getSquareClass()}
              onClick={() => onClick(i)}
            >
              {config[i]?.type === "switch" && (
                <div className="switch">
                  <LanIcon sx={{ fontSize: 56, color: "white" }} />
                </div>
              )}
              {config[i]?.type === "seat" && (
                <div className="seat-wrapper">
                  {config[i]?.participantId &&
                    users.find((u) => u.id === config[i].participantId) ? (
                    <UserCard
                      user={users.find((u) => u.id === config[i].participantId)}
                    />
                  ) : (
                    <div className="seat-circle">
                      <Typography>{config[i]?.name}</Typography>
                    </div>
                  )}
                </div>
              )}
              {config[i]?.type === "table" && (
                <div
                  className="table"
                  style={{
                    borderTop: config[i]?.borders?.top ? `3px solid ${borderColor}` : "none",
                    borderRight: config[i]?.borders?.right ? `3px solid ${borderColor}` : "none",
                    borderBottom: config[i]?.borders?.bottom ? `3px solid ${borderColor}` : "none",
                    borderLeft: config[i]?.borders?.left ? `3px solid ${borderColor}` : "none",
                  }}
                >
                  <Typography>
                    {config[i]?.number || config[i]?.name}
                  </Typography>
                </div>
              )}
            </div>
          ))}
        </Box>
      </Box>

      {/* Modal/Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Set Name / Participant</DialogTitle>
        <DialogContent>
          <form>
            {config[selectedIndex]?.type === "seat" && (
              <>
                <TextField
                  id="value"
                  label="Name"
                  name="value"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={config[selectedIndex]?.name || ""}
                  onChange={(e) => handleNameChange(e)}
                  disabled={!!config[selectedIndex]?.participantId}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth>
                  <InputLabel id="select-participant-label">
                    Participant
                  </InputLabel>
                  <Select
                    labelId="select-participant-label"
                    id="participant"
                    name="participant"
                    value={config[selectedIndex]?.participantId || ""}
                    label="participant"
                    onChange={(e) => handleParticipantChange(e)}
                  >
                    <MenuItem value="">-- None --</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.nickname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {config[selectedIndex]?.type === "table" && (
              <>
                <TextField
                  id="table-number"
                  label="Table Number"
                  name="tableNumber"
                  variant="outlined"
                  type="number"
                  fullWidth
                  value={config[selectedIndex]?.number || ""}
                  onChange={handleTableNumberChange}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle1" gutterBottom>
                  Table Borders
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(2, 1fr)"
                  gap={1}
                  sx={{ mb: 2 }}
                >
                  {["top", "right", "bottom", "left"].map((side) => (
                    <FormControl
                      key={side}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="checkbox"
                        id={`border-${side}`}
                        checked={
                          config[selectedIndex]?.borders?.[side] || false
                        }
                        onChange={handleTableBorderChange(side)}
                      />
                      <label
                        htmlFor={`border-${side}`}
                        style={{ marginLeft: 6 }}
                      >
                        {side.charAt(0).toUpperCase() + side.slice(1)}
                      </label>
                    </FormControl>
                  ))}
                </Box>
              </>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LocationPage;
